import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    SimulationInput,
    PrimordialStats,
    KratosTokens,
    SimulationConfig,
    Titan,
    Weapon,
    Gear,
    Mnemos,
    ArmoryLoadout,
    LoadoutStats,
    ArmoryItem,
    KratosTable,
    SimulationMode,
    WeaponCompareSettings,
    WeaponMatrixInput,
} from '../../core/types';
import './Simulator.css';
import '../../styles.css';
import LoadoutSection from '../loadout/LoadoutSection';
import { Constants } from '../../core/constants';
import { Utils } from '../../core/utilities';
import LoadoutStatsSection from '../loadout/LoadoutStatsSection';
import { BattlefieldSection } from '../../components/BattlefieldSection';
import { SimulatorConfigSection } from '../settings/SimulatorConfigSection';
import { ArmorySaveSection } from '../armory/ArmorySaveSection';
import DataStorageSection from '../armory/DataStorageSection';
import ArmoryEditSection from '../armory/ArmoryEditSection';
import { SpoilerConfigSection } from '../settings/SpoilerConfigSection';
import { AppTabs } from '../../core/constants/constants';
import {
    createDefaultArmoryLoadout,
    loadEffectiveArmoryFromStorage,
    loadStoredArmoryLoadouts,
    previewSharedArmoryImport,
    saveArmoryOverrides,
    saveStoredArmoryLoadouts,
} from '../../core/utilities/armory-storage';
import { loadSimulatorState, saveSimulatorState } from '../../core/utilities/ui-storage';
import {
    SavedLoadoutRecord,
    SharedLoadoutImportCandidate,
    SharedLoadoutImportRequest,
    buildSharedLoadoutEnvelopeFromCandidate,
    buildArmoryLoadoutShareEnvelope,
    buildManualLoadoutShareEnvelope,
    buildSharedLoadoutUrl,
    getRequiredSpoilerTagsForLoadoutSnapshot,
    toSharedLoadoutImportCandidate,
    toSharedLoadoutImportCandidateFromSavedRecord,
} from '../../core/utilities/loadout-sharing';
import { isFavoriteSavedLoadout, loadSavedLoadouts, removeSavedLoadout, saveSavedLoadouts, toggleFavoriteSavedLoadout } from '../../core/utilities/saved-loadouts-storage';
import { summarizeSpoilerTagsForDisplay } from '../../core/utilities/tags';
import { useCompactLayout } from '../../hooks/useCompactLayout';
import Button from '../../components/Button';
import SegmentedControl from '../../components/SegmentedControl';

function getDefaultArmoryLoadoutName(loadout: ArmoryLoadout, index: number): string {
    if (loadout.titan?.name && loadout.weapon?.name) {
        return `${loadout.titan.name} w/ ${loadout.weapon.name}`;
    }

    return `Loadout ${index + 1}`;
}

interface SimulatorProps {
    onSubmit: (input: SimulationInput) => Promise<void>;
    onMatrixSubmit: (input: WeaponMatrixInput) => Promise<void>;
    onCancelSimulation: () => void;
    canCancelSimulation: boolean;
    activeTab: AppTabs;
    onActiveTabChange: (tab: AppTabs) => void;
    simulationMode: SimulationMode;
    onSimulationModeChange: (mode: SimulationMode) => void;
    isSubmitting: boolean;
    sharedLoadoutImport: SharedLoadoutImportRequest | null;
    onSharedManualLoadoutImportApplied: (requestId: string) => void;
    onOpenSharedLoadoutCandidate: (candidate: SharedLoadoutImportCandidate) => void;
}

const Simulator: React.FC<SimulatorProps> = ({
    onSubmit,
    onMatrixSubmit,
    onCancelSimulation,
    canCancelSimulation,
    activeTab,
    onActiveTabChange,
    simulationMode,
    onSimulationModeChange,
    isSubmitting,
    sharedLoadoutImport,
    onSharedManualLoadoutImportApplied,
    onOpenSharedLoadoutCandidate,
}) => {
    const isCompact = useCompactLayout();
    const [editingItem, setEditingItem] = useState<ArmoryItem>(Constants.DEFAULT_EDITING_ITEM);
    const appliedSharedImportIdsRef = useRef<Set<string>>(new Set());
    const initialSimulatorState = useMemo(() => loadSimulatorState(), []);
    const initialStorageState = useMemo(() => {
        const effectiveArmory = loadEffectiveArmoryFromStorage();

        return {
            effectiveArmory,
            loadouts: loadStoredArmoryLoadouts(effectiveArmory),
        };
    }, []);

    const [titans, setTitans] = useState<Titan[]>(initialStorageState.effectiveArmory.titans);
    const [kratosTables, setKratosTables] = useState<KratosTable[]>(initialStorageState.effectiveArmory.kratosTables);
    const [weapons, setWeapons] = useState<Weapon[]>(initialStorageState.effectiveArmory.weapons);
    const [gear, setGear] = useState<Gear[]>(initialStorageState.effectiveArmory.gear);
    const [mnemos, setMnemos] = useState<Mnemos[]>(initialStorageState.effectiveArmory.mnemos);

    const [primordial, setPrimordial] = useState<PrimordialStats>(() => {
        const storedPrimordial = initialSimulatorState.primordialStats;
        return storedPrimordial && Object.keys(storedPrimordial).length > 0 ? storedPrimordial : { toHitValue: 7, aeonTrespassValue: 3 };
    });

    const [kratosPool, setKratosPool] = useState<KratosTokens>(() => {
        const storedKratosPool = initialSimulatorState.kratosPool;
        return storedKratosPool && Object.keys(storedKratosPool).length > 0 ? storedKratosPool : Utils.shallowCopy(Constants.EMPTY_KRATOS_POOL);
    });

    const [config, setConfig] = useState<SimulationConfig>(() => {
        const storedConfig = initialSimulatorState.simulationConfig;
        return storedConfig && Object.keys(storedConfig).length > 0 ? Utils.sanitizeInput({ config: storedConfig }).config : Constants.DEFAULT_SIMULATION_CONFIG;
    });

    const [visibleTags, setVisibleTags] = useState<string[]>(() => {
        const storedVisibleTags = initialSimulatorState.visibleTags;
        return storedVisibleTags ? Utils.expandCycleSpoilerTags(storedVisibleTags) : Constants.DEFAULT_VISIBLE_SPOILERS;
    });

    const [advancedMode, setAdvancedMode] = useState<boolean>(() => {
        return initialSimulatorState.advancedMode ?? false;
    });
    const [isFavoriteLoadoutsExpanded, setIsFavoriteLoadoutsExpanded] = useState(() => {
        const storedExpanded = typeof window !== 'undefined' ? JSON.parse(localStorage.getItem(Constants.LocalStorageKeys.FAVORITE_LOADOUTS_EXPANDED) || 'null') : null;
        return typeof storedExpanded === 'boolean' ? storedExpanded : !isCompact;
    });

    const [compareSettings, setCompareSettings] = useState<WeaponCompareSettings | null>(null);
    const [savedLoadoutRecords, setSavedLoadoutRecords] = useState<SavedLoadoutRecord[]>(() => loadSavedLoadouts());
    const [copiedFavoriteId, setCopiedFavoriteId] = useState<string | null>(null);
    const [shareToastMessage, setShareToastMessage] = useState<string | null>(null);
    const favoriteShareResetTimeoutRef = useRef<number | null>(null);
    const shareToastTimeoutRef = useRef<number | null>(null);

    const [loadoutStats, setLoadoutStats] = useState<LoadoutStats[]>(() => {
        const storedLoadoutStats = initialSimulatorState.loadoutStats;
        if (storedLoadoutStats && storedLoadoutStats.length > 0) {
            try {
                return storedLoadoutStats.map(Utils.sanitizeLoadoutStats);
            } catch (e) {
                console.error(`Error getting loadout stats from storage - ${(e as Error).message}`);
            }
        }
        return [Constants.DEFAULT_LOADOUT_STATS];
    });

    const [loadouts, setLoadouts] = useState<ArmoryLoadout[]>(initialStorageState.loadouts);
    const currentArmory = useMemo(
        () => ({
            titans,
            kratosTables,
            weapons,
            gear,
            mnemos,
        }),
        [gear, kratosTables, mnemos, titans, weapons],
    );
    const defaultArmoryLoadout = useMemo(() => createDefaultArmoryLoadout(currentArmory), [currentArmory]);

    useEffect(() => {
        saveSimulatorState({
            primordialStats: primordial,
            kratosPool,
            simulationConfig: config,
            loadoutStats,
            visibleTags,
            advancedMode,
        });
    }, [advancedMode, config, kratosPool, loadoutStats, primordial, visibleTags]);

    useEffect(() => {
        saveStoredArmoryLoadouts(loadouts, currentArmory);
    }, [currentArmory, loadouts]);

    useEffect(() => {
        saveArmoryOverrides(currentArmory);
    }, [currentArmory]);

    useEffect(() => {
        if (!sharedLoadoutImport || appliedSharedImportIdsRef.current.has(sharedLoadoutImport.requestId)) {
            return;
        }

        appliedSharedImportIdsRef.current.add(sharedLoadoutImport.requestId);

        if (sharedLoadoutImport.missingSpoilerTags.length > 0) {
            setVisibleTags((currentVisibleTags) =>
                Utils.expandCycleSpoilerTags([...currentVisibleTags, ...sharedLoadoutImport.missingSpoilerTags]),
            );
        }

        const importIntoManual = () => {
            const importedLoadouts = [Utils.sanitizeLoadoutStats(Utils.deepCopy(sharedLoadoutImport.candidate.snapshot))];
            setLoadoutStats((previousLoadouts) => {
                if (sharedLoadoutImport.mode === 'replace') {
                    return importedLoadouts.length > 0 ? importedLoadouts : [Constants.DEFAULT_LOADOUT_STATS];
                }

                return [...previousLoadouts, ...importedLoadouts];
            });
            onActiveTabChange(AppTabs.LOADOUT_STATS);
        };

        if (sharedLoadoutImport.importTarget === 'armory') {
            const armoryImportPreview = sharedLoadoutImport.armoryImportPreview;

            if (armoryImportPreview) {
                setTitans(armoryImportPreview.armory.titans);
                setKratosTables(armoryImportPreview.armory.kratosTables);
                setWeapons(armoryImportPreview.armory.weapons);
                setGear(armoryImportPreview.armory.gear);
                setMnemos(armoryImportPreview.armory.mnemos);
                setLoadouts((previousLoadouts) => {
                    if (sharedLoadoutImport.mode === 'replace') {
                        return [armoryImportPreview.loadout];
                    }

                    return [...previousLoadouts, armoryImportPreview.loadout];
                });
                onActiveTabChange(AppTabs.ARGONAUT_LOADOUTS);
            }
        } else {
            importIntoManual();
        }

        onSharedManualLoadoutImportApplied(sharedLoadoutImport.requestId);
    }, [onActiveTabChange, onSharedManualLoadoutImportApplied, sharedLoadoutImport]);

    useEffect(() => {
        saveSavedLoadouts(savedLoadoutRecords);
    }, [savedLoadoutRecords]);

    useEffect(() => {
        localStorage.setItem(Constants.LocalStorageKeys.FAVORITE_LOADOUTS_EXPANDED, JSON.stringify(isFavoriteLoadoutsExpanded));
    }, [isFavoriteLoadoutsExpanded]);

    useEffect(() => {
        return () => {
            if (favoriteShareResetTimeoutRef.current !== null) {
                window.clearTimeout(favoriteShareResetTimeoutRef.current);
            }
            if (shareToastTimeoutRef.current !== null) {
                window.clearTimeout(shareToastTimeoutRef.current);
            }
        };
    }, []);

    const memoizedOnResetSelections = useCallback(() => {
        const resetArmory = loadEffectiveArmoryFromStorage();
        setTitans(resetArmory.titans);
        setKratosTables(resetArmory.kratosTables);
        setWeapons(resetArmory.weapons);
        setGear(resetArmory.gear);
        setMnemos(resetArmory.mnemos);
        setLoadouts([createDefaultArmoryLoadout(resetArmory)]);
    }, []);

    const getLoadoutStatsFromArmory = (pool: KratosTokens = kratosPool) => {
        return loadouts.map((loadout, index) => {
            const stats = Utils.convertToLoadoutStats(loadout, pool, config);
            return {
                ...stats,
                name: loadout.name?.trim() || getDefaultArmoryLoadoutName(loadout, index),
            };
        });
    };

    const getSimulationLoadouts = (pool: KratosTokens = kratosPool) => {
        if (activeTab === AppTabs.LOADOUT_STATS) {
            return loadoutStats;
        }

        return getLoadoutStatsFromArmory(pool);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (simulationMode === 'compare') {
            if (!compareSettings || !compareSettings.hasAllBPData || compareSettings.tokens.length === 0) {
                return;
            }

            const basePool = Utils.shallowCopy(Constants.EMPTY_KRATOS_POOL);
            const scenarios = [
                {
                    id: 'base',
                    label: 'Base Pool',
                    kratosPool: basePool,
                    loadouts: getSimulationLoadouts(basePool),
                },
                ...compareSettings.tokens.flatMap((token) =>
                    Array.from({ length: compareSettings.tokenDepth }, (_, index) => {
                        const increment = index + 1;
                        const scenarioPool = {
                            ...basePool,
                            [token]: increment,
                        };
                        const tokenLabel = token.charAt(0).toUpperCase() + token.slice(1);

                        return {
                            id: `${token}-${increment}`,
                            label: `+${increment} ${tokenLabel}`,
                            groupLabel: `${tokenLabel} Impact`,
                            kratosPool: scenarioPool,
                            loadouts: getSimulationLoadouts(scenarioPool),
                        };
                    }),
                ),
            ];

            const matrixInput: WeaponMatrixInput = {
                primordialName: compareSettings.primordialName,
                primordialLevel: compareSettings.primordialLevel,
                toHitValue: compareSettings.toHitValue,
                config,
                targets: compareSettings.targets,
                scenarios,
            };

            await onMatrixSubmit(matrixInput);
            return;
        }

        const simulationInput: SimulationInput = {
            loadouts: getSimulationLoadouts(),
            primordial,
            kratosPool,
            config,
        };

        await onSubmit(simulationInput);
    };

    const handleCopyToLoadoutStats = (armoryLoadout: ArmoryLoadout) => {
        const newLoadoutStats = Utils.convertToLoadoutStats(armoryLoadout, kratosPool, config);
        const sourceIndex = Math.max(
            loadouts.findIndex((candidate) => candidate === armoryLoadout),
            0,
        );
        newLoadoutStats.name = armoryLoadout.name?.trim() || getDefaultArmoryLoadoutName(armoryLoadout, sourceIndex);
        setLoadoutStats((prevStats) => [...prevStats, newLoadoutStats]);
        onActiveTabChange(AppTabs.LOADOUT_STATS);
    };

    const copyShareUrl = useCallback(async (shareUrl: string): Promise<boolean> => {
        if (!navigator.clipboard?.writeText) {
            setShareToastMessage('Unable to copy share link.');
            return false;
        }

        try {
            await navigator.clipboard.writeText(shareUrl);
            return true;
        } catch {
            setShareToastMessage('Unable to copy share link.');
            return false;
        }
    }, []);

    useEffect(() => {
        if (!shareToastMessage) {
            return;
        }

        if (shareToastTimeoutRef.current !== null) {
            window.clearTimeout(shareToastTimeoutRef.current);
        }

        shareToastTimeoutRef.current = window.setTimeout(() => {
            setShareToastMessage(null);
        }, 2400);
    }, [shareToastMessage]);

    const buildManualImportCandidate = useCallback(
        (manualLoadout: LoadoutStats, index: number) => {
            const loadoutTitle = manualLoadout.name?.trim() || `Loadout ${index + 1}`;
            return toSharedLoadoutImportCandidate(
                buildManualLoadoutShareEnvelope(manualLoadout, {
                    sourceLoadoutName: loadoutTitle,
                }),
            );
        },
        [],
    );

    const buildArmoryImportCandidate = useCallback(
        (armoryLoadout: ArmoryLoadout, index: number) => {
            const snapshot = Utils.convertToLoadoutStats(armoryLoadout, kratosPool, config);
            const loadoutTitle = armoryLoadout.name?.trim() || getDefaultArmoryLoadoutName(armoryLoadout, index);
            snapshot.name = loadoutTitle;

            return toSharedLoadoutImportCandidate(
                buildArmoryLoadoutShareEnvelope(armoryLoadout, snapshot, {
                    sourceLoadoutName: loadoutTitle,
                }),
            );
        },
        [config, kratosPool],
    );

    const handleShareManualLoadout = useCallback(
        async (manualLoadout: LoadoutStats, index: number): Promise<boolean> => {
            const shareUrl = buildSharedLoadoutUrl(window.location.href, buildSharedLoadoutEnvelopeFromCandidate(buildManualImportCandidate(manualLoadout, index)));

            return copyShareUrl(shareUrl);
        },
        [buildManualImportCandidate, copyShareUrl],
    );

    const handleShareArmoryLoadout = useCallback(
        async (armoryLoadout: ArmoryLoadout, index: number): Promise<boolean> => {
            const shareUrl = buildSharedLoadoutUrl(window.location.href, buildSharedLoadoutEnvelopeFromCandidate(buildArmoryImportCandidate(armoryLoadout, index)));

            return copyShareUrl(shareUrl);
        },
        [buildArmoryImportCandidate, copyShareUrl],
    );

    const handleToggleFavoriteManualLoadout = useCallback(
        (manualLoadout: LoadoutStats, index: number) => {
            setSavedLoadoutRecords((currentRecords) => toggleFavoriteSavedLoadout(currentRecords, buildManualImportCandidate(manualLoadout, index)));
        },
        [buildManualImportCandidate],
    );

    const handleToggleFavoriteArmoryLoadout = useCallback(
        (armoryLoadout: ArmoryLoadout, index: number) => {
            setSavedLoadoutRecords((currentRecords) => toggleFavoriteSavedLoadout(currentRecords, buildArmoryImportCandidate(armoryLoadout, index)));
        },
        [buildArmoryImportCandidate],
    );

    const isManualLoadoutFavorite = useCallback(
        (manualLoadout: LoadoutStats, index: number) => isFavoriteSavedLoadout(savedLoadoutRecords, buildManualImportCandidate(manualLoadout, index)),
        [buildManualImportCandidate, savedLoadoutRecords],
    );

    const isArmoryLoadoutFavorite = useCallback(
        (armoryLoadout: ArmoryLoadout, index: number) => isFavoriteSavedLoadout(savedLoadoutRecords, buildArmoryImportCandidate(armoryLoadout, index)),
        [buildArmoryImportCandidate, savedLoadoutRecords],
    );

    const handleCopySavedLoadoutLink = useCallback(
        async (record: SavedLoadoutRecord) => {
            const candidate = toSharedLoadoutImportCandidateFromSavedRecord(record);
            const copied = await copyShareUrl(buildSharedLoadoutUrl(window.location.href, buildSharedLoadoutEnvelopeFromCandidate(candidate)));

            if (!copied) {
                return false;
            }

            if (favoriteShareResetTimeoutRef.current !== null) {
                window.clearTimeout(favoriteShareResetTimeoutRef.current);
            }

            setCopiedFavoriteId(record.id);
            favoriteShareResetTimeoutRef.current = window.setTimeout(() => {
                setCopiedFavoriteId(null);
            }, 1800);
            return copied;
        },
        [copyShareUrl],
    );

    const favoriteLoadouts = useMemo(() => {
        return savedLoadoutRecords
            .filter((record) => record.isFavorite)
            .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    }, [savedLoadoutRecords]);
    const favoriteLoadoutSummaries = useMemo(() => {
        return new Map(
            favoriteLoadouts.map((record) => {
                const requiredTags = new Set<string>(getRequiredSpoilerTagsForLoadoutSnapshot(record.snapshot));

                if (record.armoryRecipe) {
                    const preview = previewSharedArmoryImport(record.armoryRecipe, currentArmory, record.title);
                    preview?.requiredTags.forEach((tag) => requiredTags.add(tag));
                }

                return [record.id, summarizeSpoilerTagsForDisplay(Array.from(requiredTags).sort((a, b) => a.localeCompare(b)))];
            }),
        );
    }, [currentArmory, favoriteLoadouts]);

    const isSimulationTab = activeTab === AppTabs.ARGONAUT_LOADOUTS || activeTab === AppTabs.LOADOUT_STATS;
    const simulationTitle = activeTab === AppTabs.ARGONAUT_LOADOUTS ? 'Armory Loadouts' : 'Manual Loadouts';
    if (isSimulationTab) {
        return (
            <div className="workspace-page">
                <section className="workspace-panel simulator-toolbar">
                    <p className="workspace-card-title simulator-toolbar-title">Configuration</p>
                    <div className="mode-cluster">
                        <span className="mode-cluster-label">Input Source</span>
                        <SegmentedControl
                            ariaLabel="Input Source"
                            fullWidth
                            value={activeTab}
                            onChange={onActiveTabChange}
                            options={[
                                { value: AppTabs.ARGONAUT_LOADOUTS, label: 'Armory' },
                                { value: AppTabs.LOADOUT_STATS, label: 'Manual' },
                            ]}
                        />
                    </div>

                    <div className="mode-cluster">
                        <span className="mode-cluster-label">Output Mode</span>
                        <SegmentedControl
                            ariaLabel="Output Mode"
                            fullWidth
                            value={simulationMode}
                            onChange={onSimulationModeChange}
                            options={[
                                { value: 'standard', label: 'Attack Simulator' },
                                { value: 'compare', label: 'Loadout Analyzer' },
                            ]}
                        />
                    </div>

                    <div className="mode-cluster">
                        <span className="mode-cluster-label">Complexity</span>
                        <SegmentedControl
                            ariaLabel="Complexity"
                            fullWidth
                            value={advancedMode ? 'advanced' : 'simple'}
                            onChange={(nextValue) => setAdvancedMode(nextValue === 'advanced')}
                            options={[
                                { value: 'simple', label: 'Simple' },
                                { value: 'advanced', label: 'Advanced' },
                            ]}
                        />
                    </div>
                </section>

                <div className="simulator-workspace">
                    <aside className="simulator-control-rail">
                        <BattlefieldSection
                            primordial={primordial}
                            setPrimordial={setPrimordial}
                            kratosPool={kratosPool}
                            visibleTags={visibleTags}
                            setKratosPool={setKratosPool}
                            simulationMode={simulationMode}
                            onCompareSettingsChange={setCompareSettings}
                        />
                        <SimulatorConfigSection config={config} setConfig={setConfig} />
                        <SpoilerConfigSection
                            titans={titans}
                            kratosTables={kratosTables}
                            weapons={weapons}
                            gear={gear}
                            mnemos={mnemos}
                            visibleTags={visibleTags}
                            setVisibleTags={setVisibleTags}
                        />
                    </aside>

                    <div className="simulator-main-column">
                        <section className="workspace-panel simulator-sharing-panel">
                            <div className="panel-section-header">
                                <div className="panel-section-heading">
                                    <p className="workspace-card-title">Favorite Loadouts</p>
                                    {favoriteLoadouts.length > 0 && <p className="panel-section-summary">{favoriteLoadouts.length} saved</p>}
                                </div>
                                <Button className="panel-collapse-button" variant="ghost" size="sm" aria-expanded={isFavoriteLoadoutsExpanded} onClick={() => setIsFavoriteLoadoutsExpanded((current) => !current)}>
                                    {isFavoriteLoadoutsExpanded ? 'Hide' : 'Show'}
                                </Button>
                            </div>
                            <div className={`panel-section-body ${!isFavoriteLoadoutsExpanded ? 'panel-section-body-collapsed' : ''}`}>
                                <div className="shared-loadout-favorites-card">
                                    {favoriteLoadouts.length === 0 ? (
                                        <p className="shared-loadout-empty-state">Favorite a loadout to keep it handy here.</p>
                                    ) : (
                                        <div className="shared-loadout-favorites-list">
                                            {favoriteLoadouts.map((record) => {
                                                const spoilerTags = favoriteLoadoutSummaries.get(record.id) ?? [];

                                                return (
                                                    <div className="shared-loadout-favorite-row" key={record.id}>
                                                    <div className="shared-loadout-favorite-copy">
                                                        <p className="shared-loadout-favorite-title">{record.title}</p>
                                                        <div className="shared-loadout-favorite-meta-row">
                                                            <p className="shared-loadout-favorite-meta">{record.inputSource === 'armory' ? 'Armory source' : 'Manual source'}</p>
                                                            <div className="shared-loadout-favorite-tags" aria-label="Required spoiler tags">
                                                                {spoilerTags.length > 0 ? (
                                                                    spoilerTags.map((tag) => (
                                                                        <span className="shared-loadout-favorite-tag" key={tag}>
                                                                            {tag}
                                                                        </span>
                                                                    ))
                                                                ) : (
                                                                    <span className="shared-loadout-favorite-tag shared-loadout-favorite-tag-neutral">No spoiler tags</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="shared-loadout-favorite-actions">
                                                        <Button
                                                            className="shared-loadout-compact-action"
                                                            size="sm"
                                                            variant="ghost"
                                                            onClick={() => onOpenSharedLoadoutCandidate(toSharedLoadoutImportCandidateFromSavedRecord(record))}
                                                        >
                                                            Import
                                                        </Button>
                                                        <Button
                                                            className="shared-loadout-compact-action"
                                                            size="sm"
                                                            variant="ghost"
                                                            onClick={() => {
                                                                void handleCopySavedLoadoutLink(record);
                                                            }}
                                                        >
                                                            {copiedFavoriteId === record.id ? 'Copied Link' : 'Share'}
                                                        </Button>
                                                        <Button
                                                            className="shared-loadout-compact-action shared-loadout-compact-action-destructive"
                                                            size="sm"
                                                            variant="danger"
                                                            onClick={() => setSavedLoadoutRecords((currentRecords) => removeSavedLoadout(currentRecords, record.id))}
                                                        >
                                                            Remove
                                                        </Button>
                                                    </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </section>

                        <section className="workspace-panel simulator-main-panel">
                            <div className="simulator-main-header">
                                <p className="workspace-card-title simulator-main-title">{simulationTitle}</p>
                            </div>

                            {activeTab === AppTabs.LOADOUT_STATS ? (
                                <LoadoutStatsSection
                                    loadouts={loadoutStats}
                                    defaultLoadoutStats={Constants.DEFAULT_LOADOUT_STATS}
                                    visibleTags={visibleTags}
                                    setLoadouts={setLoadoutStats}
                                    advancedMode={advancedMode}
                                    onShareLoadout={handleShareManualLoadout}
                                    onToggleFavoriteLoadout={handleToggleFavoriteManualLoadout}
                                    isFavoriteLoadout={isManualLoadoutFavorite}
                                />
                            ) : (
                                <LoadoutSection
                                    titans={titans}
                                    kratosTables={kratosTables}
                                    weapons={weapons}
                                    gear={gear}
                                    mnemos={mnemos}
                                    loadouts={loadouts}
                                    visibleTags={visibleTags}
                                    defaultLoadout={defaultArmoryLoadout}
                                    setLoadouts={setLoadouts}
                                    handleCopyToLoadoutStats={handleCopyToLoadoutStats}
                                    advancedMode={advancedMode}
                                    onShareLoadout={handleShareArmoryLoadout}
                                    onToggleFavoriteLoadout={handleToggleFavoriteArmoryLoadout}
                                    isFavoriteLoadout={isArmoryLoadoutFavorite}
                                />
                            )}
                        </section>
                    </div>
                </div>

                <section className="workspace-panel simulator-controls-panel">
                    <form onSubmit={handleSubmit} className="simulation-input-form">
                        <div className="simulator-run-bar">
                            <p className="workspace-card-title">Controls</p>

                            <div className="run-simulation-button-container">
                                <Button
                                    type="submit"
                                    className={`run-simulation-button ${isSubmitting ? 'loading' : ''}`}
                                    size="lg"
                                    variant="accent"
                                    disabled={isSubmitting || (simulationMode === 'compare' && (!compareSettings || !compareSettings.hasAllBPData || compareSettings.tokens.length === 0))}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <span className="simulator-loading-spinner" aria-hidden="true" />
                                            <span>{simulationMode === 'compare' ? 'Analyzer Running' : 'Simulation Running'}</span>
                                        </>
                                    ) : simulationMode === 'compare' ? (
                                        'Run Loadout Analyzer'
                                    ) : (
                                        'Run Attack Simulator'
                                    )}
                                </Button>
                                <Button
                                    className="cancel-simulation-button"
                                    onClick={onCancelSimulation}
                                    size="lg"
                                    type="button"
                                    variant="danger"
                                    disabled={!isSubmitting || !canCancelSimulation}
                                >
                                    Cancel Simulation
                                </Button>
                            </div>
                        </div>
                    </form>
                </section>

                {shareToastMessage && (
                    <div className="simulator-toast" aria-live="polite" role="status">
                        {shareToastMessage}
                    </div>
                )}

            </div>
        );
    }

    return (
        <div className="workspace-page">
            <div className="library-grid">
                <div className="library-sidebar">
                    <ArmorySaveSection
                        editingItem={editingItem}
                        titans={titans}
                        kratosTables={kratosTables}
                        weapons={weapons}
                        gear={gear}
                        mnemos={mnemos}
                        visibleTags={visibleTags}
                        loadouts={loadouts}
                        defaultLoadout={defaultArmoryLoadout}
                        setEditingItem={setEditingItem}
                        setTitans={setTitans}
                        setKratosTables={setKratosTables}
                        setWeapons={setWeapons}
                        setGear={setGear}
                        setMnemos={setMnemos}
                        setVisibleTags={setVisibleTags}
                        setLoadouts={setLoadouts}
                    />
                    <DataStorageSection
                        setTitans={setTitans}
                        setKratosTables={setKratosTables}
                        setWeapons={setWeapons}
                        setGear={setGear}
                        setMnemos={setMnemos}
                        setLoadouts={setLoadouts}
                        defaultLoadout={defaultArmoryLoadout}
                        onResetSelections={memoizedOnResetSelections}
                        loadouts={loadouts}
                        titans={titans}
                        kratosTables={kratosTables}
                        weapons={weapons}
                        gear={gear}
                        mnemos={mnemos}
                    />
                </div>
                <div className="workspace-panel library-editor-panel">
                    <ArmoryEditSection visibleTags={visibleTags} editingItem={editingItem} setEditingItem={setEditingItem} />
                </div>
            </div>
        </div>
    );
};

export default React.memo(Simulator);
